/**
 * Human authority labels for the wildfire-feed `source` slugs (#603 item 8).
 *
 * The active_wildland_fires `source` column is a machine discriminator
 * ('bc-wildfire-service', 'cwfis-active-fires', …). Rendering it raw in the fire
 * panel leaks an implementation detail and reads as a bug to a government seat.
 * This maps each slug to the plain-language reporting authority (matching the
 * ingestion attribution strings), so provenance reads "BC Wildfire Service", and
 * a merged same-fire pair reads "CWFIS · also BC Wildfire Service".
 *
 * Pinned to the ingestion source constants by fireSourceLabels.drift.test.ts so a
 * new/renamed feed can't silently fall through to the raw slug.
 */

/** slug → human reporting authority. Unknown slugs fall back to the raw slug via
 *  {@link fireSourceLabel} (honest — never a fabricated authority). */
export const FIRE_SOURCE_LABELS: Record<string, string> = {
  'cwfis-active-fires': 'CWFIS (Natural Resources Canada)',
  'cwfis-m3-perimeters': 'CWFIS M3 perimeter estimate',
  'bc-wildfire-service': 'BC Wildfire Service',
  'ab-wildfire': 'Alberta Wildfire',
  'on-aviation-forest-fire': 'Ontario AFFES',
  'qc-sopfeu': 'SOPFEU (Québec)',
  'demo-seed-eyeball': 'Demo seed (illustrative)',
};

/** The plain-language authority for a feed slug, or the slug itself when unknown
 *  (honest fallback — we never invent an authority name). */
export function fireSourceLabel(source: string | null | undefined): string {
  if (source == null || source === '') return 'Unknown source';
  return FIRE_SOURCE_LABELS[source] ?? source;
}

/**
 * Provenance line for a (possibly merged) fire: the primary reporting authority,
 * with any additional same-fire feeds appended as "· also …". `sources` is the
 * canonical-first array from the API; falls back to the single `source`.
 *   ['cwfis-active-fires','bc-wildfire-service'] → "CWFIS (Natural Resources
 *   Canada) · also BC Wildfire Service"
 */
export function fireSourcesLabel(
  sources: readonly string[] | null | undefined,
  fallbackSource: string | null | undefined,
): string {
  const list =
    sources && sources.length > 0
      ? [...new Set(sources)]
      : fallbackSource
        ? [fallbackSource]
        : [];
  if (list.length === 0) return 'Unknown source';
  const labels = list.map(fireSourceLabel);
  const primary = labels[0]!;
  const rest = labels.slice(1);
  return rest.length > 0 ? `${primary} · also ${rest.join(', ')}` : primary;
}
